import { useState, useEffect, useCallback } from "react";

const API_BASE = process.env.REACT_APP_API_URL || "https://surabaya-api.onrender.com";
const C = {
  brun: "#4A2C17", brunClair: "#7A4E2D", or: "#E8C47C", orClair: "#F5E4B5",
  vert: "#2E7D32", vertClair: "#E8F5E9", rouge: "#C62828", rougeClair: "#FFEBEE",
  bleu: "#1565C0", bleuClair: "#E3F2FD", orange: "#E65100", orangeClair: "#FFF3E0",
  gris: "#F5F5F5", grisFonce: "#9E9E9E", blanc: "#FFFFFF",
};

function isConforme(temp, tMin, tMax) {
  return parseFloat(temp) >= parseFloat(tMin) && parseFloat(temp) <= parseFloat(tMax);
}

// ─── VUE RELEVÉ ───────────────────────────────────────────────────────────────
function VueReleve({ frigos, employes, onSaved }) {
  const [valeurs, setValeurs] = useState({});
  const [operateur, setOperateur] = useState("");
  const [periode, setPeriode] = useState("matin");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [errors, setErrors] = useState({});
  const actifs = frigos.filter(f => f.actif);
  const employsActifs = employes.filter(e => e.actif);

  useEffect(() => {
    if (employsActifs.length > 0 && !operateur) setOperateur(employsActifs[0].nom);
  }, [employsActifs]);

  function handleTemp(id, val) {
    setValeurs(p => ({ ...p, [id]: val }));
    setErrors(p => { const n = { ...p }; delete n[id]; return n; });
  }

  async function handleSubmit() {
    const errs = {};
    actifs.forEach(f => {
      if (valeurs[f.id] === undefined || valeurs[f.id] === "") errs[f.id] = true;
      else if (isNaN(parseFloat(valeurs[f.id]))) errs[f.id] = true;
    });
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSaving(true);
    try {
      await fetch(API_BASE + "/api/haccp/releves", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          operateur, periode,
          releves: actifs.map(f => ({
            frigo_id: f.id,
            temperature: parseFloat(valeurs[f.id]),
            conforme: isConforme(parseFloat(valeurs[f.id]), f.temp_min, f.temp_max),
          })),
        }),
      });
      setSaved(true); setValeurs({});
      if (onSaved) onSaved();
      setTimeout(() => setSaved(false), 4000);
    } catch (err) { alert("Erreur : " + err.message); }
    setSaving(false);
  }

  const now = new Date();
  const nbRemplis = actifs.filter(f => valeurs[f.id] !== undefined && valeurs[f.id] !== "").length;
  const nbNonConformes = actifs.filter(f => {
    const v = valeurs[f.id];
    return v !== undefined && v !== "" && !isNaN(parseFloat(v)) && !isConforme(parseFloat(v), f.temp_min, f.temp_max);
  }).length;

  return (
    <div translate="no" style={{ maxWidth: 480, margin: "0 auto", paddingBottom: 100 }}>
      <div style={{ background: C.blanc, borderRadius: 16, padding: "14px 18px", marginBottom: 10,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: C.brun, textTransform: "capitalize" }}>
            {now.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
          </div>
          <div style={{ fontSize: 12, color: C.grisFonce }}>
            {now.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
          </div>
        </div>
        <select translate="no" value={operateur} onChange={e => setOperateur(e.target.value)} style={{
          padding: "8px 12px", borderRadius: 20, border: "2px solid " + C.or,
          backgroundColor: C.orClair, fontWeight: 700, color: C.brun, fontSize: 14,
        }}>
          {employsActifs.map(o => <option key={o.id} value={o.nom} translate="no">{o.nom}</option>)}
        </select>
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
        {[{id:"matin",label:"Matin",icon:"🌅"},{id:"soir",label:"Soir",icon:"🌙"}].map(p => (
          <button key={p.id} onClick={() => setPeriode(p.id)} style={{
            flex: 1, padding: "12px", borderRadius: 14, border: "none", cursor: "pointer",
            backgroundColor: periode === p.id ? C.brun : C.blanc,
            color: periode === p.id ? C.or : C.brun,
            fontWeight: 700, fontSize: 16, boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
          }}>{p.icon} {p.label}</button>
        ))}
      </div>

      {actifs.length > 0 && (
        <div style={{ background: C.blanc, borderRadius: 14, padding: "12px 18px", marginBottom: 10,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
            <span style={{ fontSize: 13, color: C.grisFonce }}>{nbRemplis}/{actifs.length} saisis</span>
            {nbNonConformes > 0 && <span style={{ fontSize: 13, fontWeight: 700, color: C.rouge }}>{nbNonConformes} hors plage !</span>}
          </div>
          <div style={{ height: 6, backgroundColor: "#eee", borderRadius: 4, overflow: "hidden" }}>
            <div style={{
              height: "100%", borderRadius: 4, transition: "width 0.3s",
              width: (actifs.length > 0 ? nbRemplis / actifs.length * 100 : 0) + "%",
              backgroundColor: nbNonConformes > 0 ? C.rouge : nbRemplis === actifs.length ? C.vert : C.or,
            }} />
          </div>
        </div>
      )}

      {actifs.map(f => {
        const v = valeurs[f.id];
        const hasVal = v !== undefined && v !== "" && !isNaN(parseFloat(v));
        const ok = hasVal ? isConforme(parseFloat(v), f.temp_min, f.temp_max) : null;
        return (
          <div key={f.id} style={{
            background: ok === true ? C.vertClair : ok === false ? C.rougeClair : C.blanc,
            borderRadius: 14, padding: "14px 18px", marginBottom: 8,
            boxShadow: "0 2px 6px rgba(0,0,0,0.07)",
            border: "2px solid " + (errors[f.id] ? C.rouge : ok === true ? C.vert : ok === false ? C.rouge : "transparent"),
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1 }}>
              <span style={{ fontSize: 26 }}>{f.emoji || "🧊"}</span>
              <div style={{ fontWeight: 700, fontSize: 15, color: C.brun }}>{f.nom}</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {hasVal && <span style={{ fontSize: 13, fontWeight: 700, color: ok ? C.vert : C.rouge }}>{ok ? "OK" : "⚠"}</span>}
              <input
                type="number" step="0.1" inputMode="decimal"
                value={v !== undefined ? v : ""}
                onChange={e => handleTemp(f.id, e.target.value)}
                placeholder="°C"
                style={{
                  width: 80, padding: "10px 8px", borderRadius: 12, textAlign: "center",
                  border: "2px solid " + (errors[f.id] ? C.rouge : ok === true ? C.vert : ok === false ? C.rouge : "#ddd"),
                  fontSize: 20, fontWeight: 700, color: C.brun, backgroundColor: "white",
                }}
              />
            </div>
          </div>
        );
      })}

      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, padding: "12px 16px",
        backgroundColor: "rgba(255,255,255,0.95)", backdropFilter: "blur(8px)",
        boxShadow: "0 -2px 12px rgba(0,0,0,0.1)" }}>
        {saved ? (
          <div style={{ textAlign: "center", padding: 14, borderRadius: 12,
            backgroundColor: C.vertClair, color: C.vert, fontWeight: 700, fontSize: 16 }}>
            Enregistre !
          </div>
        ) : (
          <button onClick={handleSubmit} disabled={saving || nbRemplis < actifs.length} style={{
            width: "100%", padding: 16, borderRadius: 12, border: "none", cursor: "pointer",
            backgroundColor: (saving || nbRemplis < actifs.length) ? "#ccc" : C.brun,
            color: C.or, fontWeight: 700, fontSize: 16,
          }}>
            {saving ? "Enregistrement..." : nbRemplis < actifs.length
              ? "Remplir tous les champs (" + nbRemplis + "/" + actifs.length + ")"
              : "Enregistrer — " + (periode === "matin" ? "Matin" : "Soir")}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── VUE HISTORIQUE ───────────────────────────────────────────────────────────
function VueHistorique({ frigos }) {
  const [releves, setReleves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periode, setPeriode] = useState(7);

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(API_BASE + "/api/haccp/releves?jours=" + periode);
      const d = await r.json();
      setReleves(d.releves || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [periode]);

  useEffect(() => { charger(); }, [charger]);

  const nbNonConformes = releves.filter(r => !r.conforme).length;
  const parDate = {};
  releves.forEach(r => {
    const date = new Date(r.created_at).toLocaleDateString("fr-FR");
    if (!parDate[date]) parDate[date] = [];
    parDate[date].push(r);
  });

  return (
    <div translate="no" style={{ maxWidth: 480, margin: "0 auto" }}>
      <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center" }}>
        {[1, 7, 30].map(j => (
          <button key={j} onClick={() => setPeriode(j)} style={{
            padding: "8px 16px", borderRadius: 20, border: "none", cursor: "pointer", fontWeight: 700,
            backgroundColor: periode === j ? C.brun : "#eee", color: periode === j ? C.or : C.brun, fontSize: 13,
          }}>{j === 1 ? "Auj." : j + "j"}</button>
        ))}
        {nbNonConformes > 0 && (
          <div style={{ marginLeft: "auto", padding: "8px 14px", borderRadius: 20,
            backgroundColor: C.rougeClair, color: C.rouge, fontWeight: 700, fontSize: 13 }}>
            {nbNonConformes} anomalie(s)
          </div>
        )}
      </div>
      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: C.grisFonce }}>Chargement...</div>
      ) : Object.keys(parDate).length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: C.grisFonce }}>Aucun releve.</div>
      ) : Object.entries(parDate).map(([date, rows]) => (
        <div key={date} style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 700, color: C.brun, fontSize: 14, marginBottom: 8,
            padding: "6px 12px", backgroundColor: C.orClair, borderRadius: 10 }}>{date}</div>
          {["matin", "soir"].map(p => {
            const lignes = rows.filter(r => r.periode === p);
            if (!lignes.length) return null;
            return (
              <div key={p} style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.grisFonce, marginBottom: 6, paddingLeft: 4 }}>
                  {p === "matin" ? "🌅 Matin" : "🌙 Soir"} — {lignes[0].operateur}
                </div>
                {lignes.map(r => {
                  const frigo = frigos.find(f => String(f.id) === String(r.frigo_id));
                  return (
                    <div key={r.id} style={{
                      background: r.conforme ? C.blanc : C.rougeClair, borderRadius: 10,
                      padding: "10px 14px", marginBottom: 6,
                      borderLeft: "4px solid " + (r.conforme ? C.vert : C.rouge),
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                    }}>
                      <div style={{ fontWeight: 600, color: C.brun, fontSize: 14 }}>
                        {frigo ? frigo.emoji + " " + frigo.nom : "Frigo #" + r.frigo_id}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 18, fontWeight: 700, color: r.conforme ? C.vert : C.rouge }}>
                          {parseFloat(r.temperature).toFixed(1)}°C
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: r.conforme ? C.vert : C.rouge }}>
                          {r.conforme ? "OK" : "⚠"}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ─── VUE RANGEMENT ────────────────────────────────────────────────────────────
const ZONES = [
  { id: "haut", label: "HAUT — Zone la plus froide", icon: "⬆️", color: C.bleu, colorClair: C.bleuClair,
    temp: "0°C a +4°C",
    ok: ["Viandes crues, abats, volailles", "Poissons et fruits de mer crus", "Charcuteries ouvertes refilmees"],
    interdit: ["Produits cuits", "Fromages", "Legumes"],
    regle: "Toujours emballe hermetiquement — jamais a nu sur la grille",
  },
  { id: "milieu", label: "MILIEU — Plats prepares", icon: "↔️", color: C.vert, colorClair: C.vertClair,
    temp: "0°C a +3°C",
    ok: ["Preparations cuisinees", "Plats en attente de service", "Sauces et fonds"],
    interdit: ["Viandes ou poissons crus au-dessus des plats cuisines"],
    regle: "COUVERT obligatoire — DLC 3 jours max",
  },
  { id: "bas", label: "BAS — Bac legumes", icon: "⬇️", color: C.orange, colorClair: C.orangeClair,
    temp: "4°C a +8°C",
    ok: ["Legumes et fruits", "Fromages dans boite hermetique", "Beurre, oeufs, produits laitiers"],
    interdit: ["Viandes crues sans protection"],
    regle: "Fromages : papier sulfurise + boite hermetique",
  },
  { id: "porte", label: "PORTE — Zone tiede", icon: "🚪", color: C.grisFonce, colorClair: "#F0F0F0",
    temp: "Zone la moins froide",
    ok: ["Condiments fermes", "Sauces industrielles fermees", "Boissons"],
    interdit: ["Tout autre produit"],
    regle: "Zone la moins froide — uniquement produits stables",
  },
];

function VueRangement() {
  const [zoneOuverte, setZoneOuverte] = useState(null);
  return (
    <div translate="no" style={{ maxWidth: 480, margin: "0 auto", paddingBottom: 40 }}>
      <div style={{ background: C.rougeClair, border: "2px solid " + C.rouge, borderRadius: 14, padding: "14px 16px", marginBottom: 14 }}>
        <div style={{ fontWeight: 800, color: C.rouge, fontSize: 14, marginBottom: 4 }}>Regle absolue</div>
        <div style={{ fontSize: 13, color: C.rouge, lineHeight: 1.5 }}>
          JAMAIS de contact entre produits crus et produits cuits.
        </div>
      </div>
      <div style={{ background: C.orClair, border: "2px solid " + C.or, borderRadius: 14, padding: "14px 16px", marginBottom: 14 }}>
        <div style={{ fontWeight: 800, color: C.brun, fontSize: 14, marginBottom: 8 }}>Etiquetage obligatoire</div>
        {[["1","Nom du produit","Sauce morilles"],["2","Date de fabrication","Fait le 06/03/2026"],["3","DLC","A conso avant le 09/03/2026"]].map(([n,l,e]) => (
          <div key={n} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <div style={{ width: 26, height: 26, borderRadius: "50%", backgroundColor: C.brun, color: C.or,
              fontWeight: 800, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{n}</div>
            <div style={{ fontSize: 13 }}>
              <span style={{ fontWeight: 700, color: C.brun }}>{l}</span>
              <span style={{ color: C.grisFonce }}> — ex : {e}</span>
            </div>
          </div>
        ))}
      </div>
      {ZONES.map(zone => (
        <div key={zone.id} style={{ marginBottom: 10 }}>
          <div onClick={() => setZoneOuverte(zoneOuverte === zone.id ? null : zone.id)} style={{
            background: zoneOuverte === zone.id ? zone.color : C.blanc,
            borderRadius: zoneOuverte === zone.id ? "14px 14px 0 0" : 14,
            padding: "14px 16px", cursor: "pointer", border: "2px solid " + zone.color,
            display: "flex", alignItems: "center", gap: 12,
          }}>
            <span style={{ fontSize: 22 }}>{zone.icon}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: zoneOuverte === zone.id ? C.blanc : zone.color }}>{zone.label}</div>
              <div style={{ fontSize: 12, color: zoneOuverte === zone.id ? "rgba(255,255,255,0.8)" : C.grisFonce }}>{zone.temp}</div>
            </div>
            <span style={{ color: zoneOuverte === zone.id ? C.blanc : zone.color }}>{zoneOuverte === zone.id ? "▲" : "▼"}</span>
          </div>
          {zoneOuverte === zone.id && (
            <div style={{ background: zone.colorClair, border: "2px solid " + zone.color, borderTop: "none", borderRadius: "0 0 14px 14px", padding: "14px 16px" }}>
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontWeight: 700, color: zone.color, fontSize: 13, marginBottom: 6 }}>Ce qui va la</div>
                {zone.ok.map((item, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, marginBottom: 5 }}>
                    <span style={{ color: C.vert, fontWeight: 800 }}>✓</span>
                    <span style={{ fontSize: 13, color: C.brun }}>{item}</span>
                  </div>
                ))}
              </div>
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontWeight: 700, color: C.rouge, fontSize: 13, marginBottom: 6 }}>Ce qui n y va PAS</div>
                {zone.interdit.map((item, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, marginBottom: 5 }}>
                    <span style={{ color: C.rouge, fontWeight: 800 }}>✗</span>
                    <span style={{ fontSize: 13, color: C.rouge }}>{item}</span>
                  </div>
                ))}
              </div>
              <div style={{ background: "rgba(255,255,255,0.7)", borderRadius: 10, padding: "10px 14px", borderLeft: "3px solid " + zone.color }}>
                <div style={{ fontSize: 12, color: C.brun, fontStyle: "italic" }}>{zone.regle}</div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── SECTION GENERIQUE (frigos ou employes) avec fleches ordre ────────────────
function SectionOrdonnable({ titre, items, onMoveUp, onMoveDown, onToggle, onEdit, onSupprimer, renderLabel }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontWeight: 800, color: C.brun, fontSize: 15, marginBottom: 10,
        paddingBottom: 6, borderBottom: "2px solid " + C.or }}>{titre}</div>
      {items.map((item, idx) => (
        <div key={item.id} style={{
          background: item.actif ? C.blanc : "#f5f5f5", borderRadius: 14, padding: "12px 14px", marginBottom: 8,
          boxShadow: "0 1px 6px rgba(0,0,0,0.07)", opacity: item.actif ? 1 : 0.6,
          border: "1px solid " + (item.actif ? C.or : "#ddd"),
          display: "flex", alignItems: "center", gap: 10,
        }}>
          {/* Fleches ordre */}
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <button onClick={() => onMoveUp(idx)} disabled={idx === 0} style={{
              width: 28, height: 28, borderRadius: 6, border: "1px solid #ddd", cursor: idx === 0 ? "default" : "pointer",
              backgroundColor: idx === 0 ? "#f5f5f5" : C.orClair, color: C.brun, fontSize: 14,
              display: "flex", alignItems: "center", justifyContent: "center", opacity: idx === 0 ? 0.3 : 1,
            }}>▲</button>
            <button onClick={() => onMoveDown(idx)} disabled={idx === items.length - 1} style={{
              width: 28, height: 28, borderRadius: 6, border: "1px solid #ddd", cursor: idx === items.length - 1 ? "default" : "pointer",
              backgroundColor: idx === items.length - 1 ? "#f5f5f5" : C.orClair, color: C.brun, fontSize: 14,
              display: "flex", alignItems: "center", justifyContent: "center", opacity: idx === items.length - 1 ? 0.3 : 1,
            }}>▼</button>
          </div>
          {/* Label */}
          <div style={{ flex: 1 }}>{renderLabel(item)}</div>
          {/* Actions */}
          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
            <button onClick={() => onToggle(item)} style={{
              padding: "6px 10px", borderRadius: 8, border: "1px solid " + C.or,
              cursor: "pointer", backgroundColor: C.orClair, color: C.brun, fontWeight: 700, fontSize: 12,
            }}>{item.actif ? "OFF" : "ON"}</button>
            {onEdit && <button onClick={() => onEdit(item)} style={{
              padding: "6px 10px", borderRadius: 8, border: "1px solid " + C.bleu,
              cursor: "pointer", backgroundColor: C.bleuClair, color: C.bleu, fontSize: 12,
            }}>✏️</button>}
            <button onClick={() => onSupprimer(item.id)} style={{
              padding: "6px 10px", borderRadius: 8, border: "1px solid " + C.rouge,
              cursor: "pointer", backgroundColor: C.rougeClair, color: C.rouge, fontSize: 12,
            }}>🗑</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── VUE CONFIG ───────────────────────────────────────────────────────────────
function VueConfig({ frigos, employes, onRefresh }) {
  const EMOJIS = ["🧊", "🥩", "🐟", "🍲", "🧀", "🥦", "❄️", "🍷", "🥗", "🏪"];
  const [formFrigo, setFormFrigo] = useState(null);
  const [formEmploye, setFormEmploye] = useState(null);
  const [saving, setSaving] = useState(false);

  // ── Helpers ordre ──
  async function sauvegarderOrdre(liste, endpoint) {
    for (let i = 0; i < liste.length; i++) {
      await fetch(API_BASE + endpoint + liste[i].id, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...liste[i], ordre: i }),
      });
    }
    onRefresh();
  }

  function moveItem(liste, idx, dir, endpoint) {
    const newListe = [...liste];
    const swap = idx + dir;
    if (swap < 0 || swap >= newListe.length) return;
    [newListe[idx], newListe[swap]] = [newListe[swap], newListe[idx]];
    sauvegarderOrdre(newListe, endpoint);
  }

  // ── Frigos ──
  async function toggleFrigo(frigo) {
    await fetch(API_BASE + "/api/haccp/frigos/" + frigo.id, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...frigo, actif: !frigo.actif }),
    });
    onRefresh();
  }

  async function supprimerFrigo(id) {
    if (!window.confirm("Supprimer cet appareil ?")) return;
    await fetch(API_BASE + "/api/haccp/frigos/" + id, { method: "DELETE" });
    onRefresh();
  }

  async function sauvegarderFrigo() {
    if (!formFrigo.nom || formFrigo.temp_min === "" || formFrigo.temp_max === "") { alert("Nom + temperatures requis."); return; }
    setSaving(true);
    const url = formFrigo.id ? API_BASE + "/api/haccp/frigos/" + formFrigo.id : API_BASE + "/api/haccp/frigos";
    await fetch(url, {
      method: formFrigo.id ? "PUT" : "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...formFrigo, temp_min: parseFloat(formFrigo.temp_min), temp_max: parseFloat(formFrigo.temp_max) }),
    });
    setFormFrigo(null); setSaving(false); onRefresh();
  }

  // ── Employes ──
  async function toggleEmploye(emp) {
    await fetch(API_BASE + "/api/haccp/employes/" + emp.id, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...emp, actif: !emp.actif }),
    });
    onRefresh();
  }

  async function supprimerEmploye(id) {
    if (!window.confirm("Supprimer cet employe ?")) return;
    await fetch(API_BASE + "/api/haccp/employes/" + id, { method: "DELETE" });
    onRefresh();
  }

  async function sauvegarderEmploye() {
    if (!formEmploye.nom) { alert("Nom requis."); return; }
    setSaving(true);
    const url = formEmploye.id ? API_BASE + "/api/haccp/employes/" + formEmploye.id : API_BASE + "/api/haccp/employes";
    await fetch(url, {
      method: formEmploye.id ? "PUT" : "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...formEmploye, ordre: formEmploye.ordre || employes.length }),
    });
    setFormEmploye(null); setSaving(false); onRefresh();
  }

  return (
    <div translate="no" style={{ maxWidth: 480, margin: "0 auto", paddingBottom: 60 }}>

      {/* ── APPAREILS ── */}
      <button onClick={() => setFormFrigo({ nom: "", emoji: "🧊", temp_min: "", temp_max: "", actif: true, ordre: frigos.length })} style={{
        width: "100%", padding: 14, borderRadius: 12, border: "none", cursor: "pointer",
        backgroundColor: C.brun, color: C.or, fontWeight: 700, fontSize: 15, marginBottom: 14,
      }}>+ Ajouter un appareil</button>

      {formFrigo && (
        <div style={{ background: C.orClair, borderRadius: 16, padding: 18, marginBottom: 16, border: "2px solid " + C.or }}>
          <div style={{ fontWeight: 700, color: C.brun, marginBottom: 12 }}>{formFrigo.id ? "Modifier l'appareil" : "Nouvel appareil"}</div>
          <input value={formFrigo.nom} onChange={e => setFormFrigo(p => ({ ...p, nom: e.target.value }))} placeholder="Nom (ex: Frigo 1)"
            style={{ width: "100%", padding: 12, borderRadius: 10, border: "1px solid #ccc", marginBottom: 10, fontSize: 15, boxSizing: "border-box" }} />
          <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
            {EMOJIS.map(em => (
              <button key={em} onClick={() => setFormFrigo(p => ({ ...p, emoji: em }))} style={{
                width: 38, height: 38, fontSize: 20, borderRadius: 8, cursor: "pointer",
                border: "2px solid " + (formFrigo.emoji === em ? C.brun : "#ddd"),
                backgroundColor: formFrigo.emoji === em ? C.orClair : "white",
              }}>{em}</button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
            <input type="number" step="0.5" value={formFrigo.temp_min} onChange={e => setFormFrigo(p => ({ ...p, temp_min: e.target.value }))} placeholder="Min °C"
              style={{ flex: 1, padding: 12, borderRadius: 10, border: "1px solid #ccc", fontSize: 15 }} />
            <input type="number" step="0.5" value={formFrigo.temp_max} onChange={e => setFormFrigo(p => ({ ...p, temp_max: e.target.value }))} placeholder="Max °C"
              style={{ flex: 1, padding: 12, borderRadius: 10, border: "1px solid #ccc", fontSize: 15 }} />
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => setFormFrigo(null)} style={{ flex: 1, padding: 12, borderRadius: 10, border: "1px solid #ccc", cursor: "pointer", backgroundColor: "white" }}>Annuler</button>
            <button onClick={sauvegarderFrigo} disabled={saving} style={{ flex: 2, padding: 12, borderRadius: 10, border: "none", cursor: "pointer", backgroundColor: C.brun, color: C.or, fontWeight: 700 }}>
              {saving ? "..." : "Sauvegarder"}
            </button>
          </div>
        </div>
      )}

      <SectionOrdonnable
        titre="Appareils"
        items={frigos}
        onMoveUp={(idx) => moveItem(frigos, idx, -1, "/api/haccp/frigos/")}
        onMoveDown={(idx) => moveItem(frigos, idx, 1, "/api/haccp/frigos/")}
        onToggle={toggleFrigo}
        onEdit={(f) => setFormFrigo({ ...f })}
        onSupprimer={supprimerFrigo}
        renderLabel={(f) => (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 24 }}>{f.emoji || "🧊"}</span>
            <div>
              <div style={{ fontWeight: 700, color: C.brun, fontSize: 14 }}>{f.nom}</div>
              <div style={{ fontSize: 11, color: C.grisFonce }}>{f.temp_min}°C → {f.temp_max}°C</div>
            </div>
            <span style={{ marginLeft: "auto", padding: "3px 8px", borderRadius: 8, fontSize: 11, fontWeight: 700,
              backgroundColor: f.actif ? C.vertClair : "#eee", color: f.actif ? C.vert : C.grisFonce }}>
              {f.actif ? "Actif" : "Inactif"}
            </span>
          </div>
        )}
      />

      {/* ── EMPLOYES ── */}
      <button onClick={() => setFormEmploye({ nom: "", actif: true })} style={{
        width: "100%", padding: 14, borderRadius: 12, border: "none", cursor: "pointer",
        backgroundColor: C.brunClair, color: C.or, fontWeight: 700, fontSize: 15, marginBottom: 14,
      }}>+ Ajouter un employe</button>

      {formEmploye && (
        <div style={{ background: C.orClair, borderRadius: 16, padding: 18, marginBottom: 16, border: "2px solid " + C.or }}>
          <div style={{ fontWeight: 700, color: C.brun, marginBottom: 12 }}>{formEmploye.id ? "Modifier" : "Nouvel employe"}</div>
          <input value={formEmploye.nom} onChange={e => setFormEmploye(p => ({ ...p, nom: e.target.value }))} placeholder="Prenom"
            style={{ width: "100%", padding: 12, borderRadius: 10, border: "1px solid #ccc", marginBottom: 10, fontSize: 15, boxSizing: "border-box" }} />
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => setFormEmploye(null)} style={{ flex: 1, padding: 12, borderRadius: 10, border: "1px solid #ccc", cursor: "pointer", backgroundColor: "white" }}>Annuler</button>
            <button onClick={sauvegarderEmploye} disabled={saving} style={{ flex: 2, padding: 12, borderRadius: 10, border: "none", cursor: "pointer", backgroundColor: C.brun, color: C.or, fontWeight: 700 }}>
              {saving ? "..." : "Sauvegarder"}
            </button>
          </div>
        </div>
      )}

      <SectionOrdonnable
        titre="Employes"
        items={employes}
        onMoveUp={(idx) => moveItem(employes, idx, -1, "/api/haccp/employes/")}
        onMoveDown={(idx) => moveItem(employes, idx, 1, "/api/haccp/employes/")}
        onToggle={toggleEmploye}
        onEdit={(e) => setFormEmploye({ ...e })}
        onSupprimer={supprimerEmploye}
        renderLabel={(e) => (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 20 }}>👤</span>
            <div style={{ fontWeight: 700, color: C.brun }}>{e.nom}</div>
            <span style={{ marginLeft: "auto", padding: "3px 8px", borderRadius: 8, fontSize: 11, fontWeight: 700,
              backgroundColor: e.actif ? C.vertClair : "#eee", color: e.actif ? C.vert : C.grisFonce }}>
              {e.actif ? "Actif" : "Inactif"}
            </span>
          </div>
        )}
      />
    </div>
  );
}
// —— VUE RÉCEPTIONS ————————————————————————————————————————————
function VueReceptions({ employes }) {
  const [receptions, setReceptions] = useState([]);
  const [form, setForm] = useState({ type_produit: "viande", fournisseur: "", numero_lot: "", dlc: "", temperature_arrivee: "", conformite: "conforme", notes: "", employe_nom: "" });
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("form"); // form | liste

  useEffect(() => { chargerReceptions(); }, []);

  async function chargerReceptions() {
    try {
      const r = await fetch(API_BASE + "/api/haccp/receptions");
      setReceptions(await r.json());
    } catch(e) { console.error(e); }
  }

  function handlePhoto(e) {
    const f = e.target.files[0];
    if (!f) return;
    setPhoto(f);
    setPreview(URL.createObjectURL(f));
  }

  async function handleSubmit() {
    if (!photo) return alert("Photo obligatoire");
    if (!form.type_produit) return alert("Type produit obligatoire");
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("photo", photo);
      const r = await fetch(API_BASE + "/api/haccp/receptions", { method: "POST", body: fd });
      if (!r.ok) throw new Error(await r.text());
      setForm({ type_produit: "viande", fournisseur: "", numero_lot: "", dlc: "", temperature_arrivee: "", conformite: "conforme", notes: "", employe_nom: "" });
      setPhoto(null); setPreview(null);
      chargerReceptions();
      setView("liste");
    } catch(e) { alert("Erreur : " + e.message); }
    setSaving(false);
  }

  const employesActifs = employes.filter(e => e.actif);
  const badge = (c) => ({ conforme: { bg: "#E8F5E9", color: "#2E7D32" }, non_conforme: { bg: "#FFEBEE", color: "#C62828" }, reserve: { bg: "#FFF8E1", color: "#F57F17" } }[c] || {});

  return (
    <div translate="no" style={{ maxWidth: 520, margin: "0 auto" }}>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {["form","liste"].map(v => (
          <button key={v} onClick={() => setView(v)} style={{ flex: 1, padding: "10px", borderRadius: 10, border: "none", cursor: "pointer", backgroundColor: view === v ? C.brun : "rgba(255,255,255,0.15)", color: view === v ? C.blanc : C.or, fontWeight: 700, fontSize: 13 }}>
            {v === "form" ? "➕ Nouvelle réception" : `📋 Historique (${receptions.length})`}
          </button>
        ))}
      </div>

      {view === "form" && (
        <div style={{ background: C.blanc, borderRadius: 16, padding: 20, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
          <div style={{ display: "grid", gap: 12 }}>
            <select value={form.type_produit} onChange={e => setForm(p => ({...p, type_produit: e.target.value}))} style={{ padding: "10px 14px", borderRadius: 10, border: "2px solid " + C.or, backgroundColor: C.orClair, fontWeight: 700, color: C.brun, fontSize: 14 }}>
              {["viande","poisson","légumes","produits_laitiers","épicerie","autre"].map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)}
            </select>
            <input placeholder="Fournisseur" value={form.fournisseur} onChange={e => setForm(p => ({...p, fournisseur: e.target.value}))} style={{ padding: "10px 14px", borderRadius: 10, border: "2px solid #eee", fontSize: 14 }} />
            <input placeholder="N° lot" value={form.numero_lot} onChange={e => setForm(p => ({...p, numero_lot: e.target.value}))} style={{ padding: "10px 14px", borderRadius: 10, border: "2px solid #eee", fontSize: 14 }} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <div><label style={{ fontSize: 11, color: C.grisFonce, marginBottom: 4, display: "block" }}>DLC</label><input type="date" value={form.dlc} onChange={e => setForm(p => ({...p, dlc: e.target.value}))} style={{ padding: "10px 14px", borderRadius: 10, border: "2px solid #eee", fontSize: 14, width: "100%", boxSizing: "border-box" }} /></div>
              <div><label style={{ fontSize: 11, color: C.grisFonce, marginBottom: 4, display: "block" }}>Temp. arrivée (°C)</label><input type="number" step="0.1" placeholder="ex: 4.2" value={form.temperature_arrivee} onChange={e => setForm(p => ({...p, temperature_arrivee: e.target.value}))} style={{ padding: "10px 14px", borderRadius: 10, border: "2px solid #eee", fontSize: 14, width: "100%", boxSizing: "border-box" }} /></div>
            </div>
            <select value={form.conformite} onChange={e => setForm(p => ({...p, conformite: e.target.value}))} style={{ padding: "10px 14px", borderRadius: 10, border: "2px solid " + (form.conformite === "conforme" ? "#4CAF50" : form.conformite === "non_conforme" ? "#f44336" : "#FF9800"), fontWeight: 700, color: form.conformite === "conforme" ? "#2E7D32" : form.conformite === "non_conforme" ? "#C62828" : "#E65100", fontSize: 14 }}>
              <option value="conforme">✅ Conforme</option>
              <option value="non_conforme">❌ Non conforme</option>
              <option value="reserve">⚠️ Réserve</option>
            </select>
            <select value={form.employe_nom} onChange={e => setForm(p => ({...p, employe_nom: e.target.value}))} style={{ padding: "10px 14px", borderRadius: 10, border: "2px solid #eee", fontSize: 14 }}>
              <option value="">— Employé —</option>
              {employesActifs.map(e => <option key={e.id} value={e.nom}>{e.nom}</option>)}
            </select>
            <textarea placeholder="Notes..." value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} rows={2} style={{ padding: "10px 14px", borderRadius: 10, border: "2px solid #eee", fontSize: 14, resize: "vertical" }} />
            <div>
              <label style={{ display: "block", padding: "12px", borderRadius: 10, border: "2px dashed " + C.or, textAlign: "center", cursor: "pointer", backgroundColor: C.orClair, color: C.brun, fontWeight: 700 }}>
                {preview ? <img src={preview} alt="preview" style={{ maxHeight: 120, borderRadius: 8 }} /> : "📷 Photo du bon de livraison"}
                <input type="file" accept="image/*" capture="environment" onChange={handlePhoto} style={{ display: "none" }} />
              </label>
            </div>
            <button onClick={handleSubmit} disabled={saving} style={{ padding: "14px", borderRadius: 12, border: "none", backgroundColor: C.brun, color: C.blanc, fontWeight: 800, fontSize: 15, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1 }}>
              {saving ? "Enregistrement..." : "✅ Enregistrer la réception"}
            </button>
          </div>
        </div>
      )}

      {view === "liste" && (
        <div style={{ display: "grid", gap: 10 }}>
          {receptions.length === 0 && <div style={{ textAlign: "center", color: C.grisFonce, padding: 40 }}>Aucune réception enregistrée</div>}
          {receptions.map(r => (
            <div key={r.id} style={{ background: C.blanc, borderRadius: 14, padding: 14, boxShadow: "0 2px 6px rgba(0,0,0,0.07)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontWeight: 800, color: C.brun, fontSize: 15 }}>{r.type_produit.charAt(0).toUpperCase()+r.type_produit.slice(1)} {r.fournisseur ? `— ${r.fournisseur}` : ""}</div>
                <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, backgroundColor: badge(r.conformite).bg, color: badge(r.conformite).color }}>{r.conformite}</span>
              </div>
              <div style={{ display: "flex", gap: 16, fontSize: 12, color: C.grisFonce, marginBottom: 8 }}>
                {r.temperature_arrivee && <span>🌡 {r.temperature_arrivee}°C</span>}
                {r.dlc && <span>📅 DLC : {new Date(r.dlc).toLocaleDateString("fr-FR")}</span>}
                {r.employe_nom && <span>👤 {r.employe_nom}</span>}
              </div>
              {r.notes && <div style={{ fontSize: 12, color: C.grisFonce, marginBottom: 8 }}>{r.notes}</div>}
              {r.photo_url && <img src={r.photo_url} alt="réception" style={{ width: "100%", borderRadius: 10, maxHeight: 160, objectFit: "cover" }} />}
              <div style={{ fontSize: 11, color: C.grisFonce, marginTop: 6 }}>{new Date(r.date_reception).toLocaleString("fr-FR")}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
// ─── MODULE PRINCIPAL ─────────────────────────────────────────────────────────
export default function HACCPModule() {
  const [onglet, setOnglet] = useState("releve");
  const [frigos, setFrigos] = useState([]);
  const [employes, setEmployes] = useState([]);
  const [loading, setLoading] = useState(true);

  const charger = useCallback(async () => {
    try {
      const [rf, re] = await Promise.all([
        fetch(API_BASE + "/api/haccp/frigos").then(r => r.json()),
        fetch(API_BASE + "/api/haccp/employes").then(r => r.json()),
      ]);
      setFrigos((rf.frigos || []).sort((a, b) => (a.ordre || 0) - (b.ordre || 0)));
      setEmployes((re.employes || []).sort((a, b) => (a.ordre || 0) - (b.ordre || 0)));
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const TABS = [
    { id: "releve",     label: "Releve",    icon: "📋" },
    { id: "rangement",  label: "Rangement", icon: "📦" },
    { id: "historique", label: "Historique",icon: "📊" },
    { id: "config",     label: "Config",    icon: "⚙️" },
    { id: "receptions", label: "Réceptions", icon: "📦" },
  ];

  return (
    <div translate="no" style={{ minHeight: "100vh", backgroundColor: "#F2EDE8", fontFamily: "system-ui, -apple-system, sans-serif" }}>
      <div style={{ background: "linear-gradient(135deg, " + C.brun + ", " + C.brunClair + ")", padding: "20px 20px 0", color: C.or }}>
        <div style={{ fontSize: 20, fontWeight: 800 }}>HACCP Temperatures</div>
        <div style={{ fontSize: 13, opacity: 0.8, marginTop: 2 }}>
          {frigos.filter(f => f.actif).length} appareil(s) — Surabaya Cafe
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: 14 }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setOnglet(t.id)} style={{
              flex: 1, padding: "10px 4px", borderRadius: "10px 10px 0 0", border: "none", cursor: "pointer",
              backgroundColor: onglet === t.id ? "#F2EDE8" : "rgba(255,255,255,0.15)",
              color: onglet === t.id ? C.brun : C.or, fontWeight: 700, fontSize: 11,
            }}>
              <div>{t.icon}</div>
              <div>{t.label}</div>
            </button>
          ))}
        </div>
      </div>
      <div style={{ padding: 16 }}>
        {loading ? (
          <div style={{ padding: 60, textAlign: "center", color: C.grisFonce }}>Chargement...</div>
        ) : (
          <>
            {onglet === "releve"     && <VueReleve frigos={frigos} employes={employes} onSaved={charger} />}
            {onglet === "rangement"  && <VueRangement />}
            {onglet === "historique" && <VueHistorique frigos={frigos} />}
            {onglet === "config"     && <VueConfig frigos={frigos} employes={employes} onRefresh={charger} />}
            {onglet === "receptions" && <VueReceptions employes={employes} />}
          </>
        )}
      </div>
    </div>
  );
}
